/* eslint-disable @typescript-eslint/no-unused-vars */
import { RichTextEntries } from '@/constants';

import {
  type IEvent,
  type IEventWithoutSensitiveDataProps,
  type IRichText,
  type RTButton,
  type RTLeadGenBanner,
  EventLocation,
  EventAttendanceMode,
  type IEventStructuredData,
} from '@/interfaces';

import { removeManyButtonSensitiveData, removeOneButtonSensitiveData } from './buttonDataModify.utils';
import { removeRichtextNextSiblings } from './richtext.utils';

type RemoveOneEventSensitiveData = (event: IEvent) => Omit<IEvent, 'video'>;

export const removeOneEventSensitiveData: RemoveOneEventSensitiveData = (event) => {
  delete event.video;
  const hasLinkedEntry = event.content?.links.entries.block.length;

  if (!hasLinkedEntry) {
    return {
      ...event,
      hasWall: false,
    };
  }

  const { data: modifiedContent, hasEntry } = removeRichtextNextSiblings(
    event.content as IRichText,
    RichTextEntries.RegistrationWall,
  );

  const modifiedLinkedEntriesBlock = modifiedContent.links.entries.block.map((item) => {
    const itemTypename = item?.__typename;

    switch (itemTypename) {
      case RichTextEntries.Button: {
        const button = item as RTButton;

        return removeOneButtonSensitiveData(button);
      }

      case RichTextEntries.LeadGenBanner: {
        const leadGenBanner = item as RTLeadGenBanner;
        const hasButtons = !!leadGenBanner.buttons?.length;

        if (!hasButtons) return leadGenBanner;

        const modifiedButtons = removeManyButtonSensitiveData(leadGenBanner.buttons || []);

        return {
          ...leadGenBanner,
          buttons: modifiedButtons,
        };
      }

      default: {
        return item;
      }
    }
  });

  return {
    ...event,
    content: {
      ...modifiedContent,
      links: {
        ...modifiedContent.links,
        entries: {
          block: modifiedLinkedEntriesBlock,
        },
      },
    },
    hasWall: hasEntry,
  } as IEventWithoutSensitiveDataProps;
};

export const getEventStructuredData = (event: IEvent) => {
  const data = {
    '@context': 'https://schema.org/',
    '@type': 'Event',
    location: {
      '@type': event.location === 'Virtual' ? EventLocation.VirtualLocation : EventLocation.Place,
    },
    eventAttendanceMode: event.location === 'Virtual' ? EventAttendanceMode.Online : EventAttendanceMode.Offline,
    name: event.title,
    startDate: event.startDate,
    description: event.seo?.description,
    endDate: event.endDate,
    image: event.image?.url,
  };

  if (event.location === 'Physical') {
    (data as IEventStructuredData).location.name = event.address;
    (data as IEventStructuredData).location.address = {
      '@type': 'PostalAddress',
      name: event.address,
    };
  } else {
    (data as IEventStructuredData).location.url = event.buttonLink;
  }

  if (event.organizer) {
    (data as IEventStructuredData).organizer = {
      '@type': 'Organization',
      name: event.organizer?.companyName,
      url: event.organizer?.companyLink,
    };
  }

  return data;
};
